"use client"

import { motion } from "framer-motion"
import { Check, Code2, Blocks } from "lucide-react"
import ghost from '../Images/ghost.png'
export default function IntegrationsSection() {
  const container = {
    hidden: { opacity: 0 },
    show: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2,
      },
    },
  }

  const item = {
    hidden: { opacity: 0, y: 20 },
    show: { opacity: 1, y: 0 },
  }

  return (
    <section 
    id="integrations"
    className="relative bg-gradient-to-b from-slate-900 via-slate-900 to-slate-900/90 py-24 overflow-hidden">
      {/* Background pattern */}
      <div className="absolute inset-0 bg-grid-white/5 bg-grid [mask-image:radial-gradient(white,transparent_70%)]" />

      <div className="container mx-auto px-4 md:px-6 relative">
        <div className="grid lg:grid-cols-2 gap-16 lg:gap-24 items-center">
          {/* Left column with logos */}
          <motion.div
            variants={container}
            initial="hidden"
            whileInView="show"
            viewport={{ once: true }}
            className="space-y-12"
          >
            <div className="grid grid-cols-2 gap-8">
              {/* WordPress Icon */}
              <motion.div variants={item} whileHover={{ scale: 1.05 }} className="relative group">
                <div className="absolute -inset-0.5 bg-gradient-to-r from-blue-600 to-indigo-600 rounded-2xl blur opacity-75 group-hover:opacity-100 transition duration-200" />
                <div className="relative flex items-center justify-center p-8 rounded-2xl bg-gradient-to-r from-blue-600/90 to-indigo-600/90 shadow-2xl">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 512 512"
                    className="w-16 h-16 text-white drop-shadow-lg"
                  >
                    <path
                      fill="currentColor"
                      d="M61.7 169.4l101.5 278C92.2 413 43.3 340.2 43.3 256c0-30.9 6.6-60.1 18.4-86.6zm337.9 75.9c0-26.3-9.4-44.5-17.5-58.7-10.8-17.5-20.9-32.4-20.9-49.9 0-19.6 14.8-37.8 35.7-37.8.9 0 1.8.1 2.8.2-37.9-34.7-88.3-55.9-143.7-55.9-74.3 0-139.7 38.1-177.8 95.9 5 .2 9.7.3 13.7.3 22.2 0 56.7-2.7 56.7-2.7 11.5-.7 12.8 16.2 1.4 17.5 0 0-11.5 1.3-24.3 2l77.5 230.4L249.8 247l-33.1-90.8c-11.5-.7-22.3-2-22.3-2-11.5-.7-10.1-18.2 1.3-17.5 0 0 35.1 2.7 56 2.7 22.2 0 56.7-2.7 56.7-2.7 11.5-.7 12.8 16.2 1.4 17.5 0 0-11.5 1.3-24.3 2l76.9 228.7 21.2-70.9c9-29.4 16-50.5 16-68.7z"
                    />
                  </svg>
                </div>
              </motion.div>

              {/* Ghost Logo */}
              <motion.div variants={item} whileHover={{ scale: 1.05 }} className="relative group">
                <div className="absolute -inset-0.5 bg-gradient-to-r from-gray-600 to-gray-800 rounded-2xl blur opacity-75 group-hover:opacity-100 transition duration-200" />
                <div className="relative flex items-center justify-center p-8 rounded-2xl bg-gradient-to-r from-gray-700/90 to-gray-900/90 shadow-2xl">
                  <img
                    src={ghost}
                    alt="Ghost Logo"
                    width={64}
                    height={64}
                    className="w-16 h-16 object-contain invert brightness-200 drop-shadow-lg"
                  />
                </div>
              </motion.div>

              {/* API Block */}
              <motion.div variants={item} whileHover={{ scale: 1.05 }} className="relative col-span-2 group">
                <div className="absolute -inset-0.5 bg-gradient-to-r from-emerald-500 to-teal-500 rounded-2xl blur opacity-75 group-hover:opacity-100 transition duration-200" />
                <div className="relative flex items-center justify-center gap-3 p-8 rounded-2xl bg-gradient-to-r from-emerald-500/90 to-teal-500/90 shadow-2xl">
                  <Code2 className="w-8 h-8 text-white" />
                  <span className="text-white text-2xl font-mono font-bold tracking-tight">API</span>
                </div>
              </motion.div>
            </div>
          </motion.div>

          {/* Right column with content */}
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            className="space-y-8"
          >
            <div className="space-y-4">
              <h2 className="text-4xl md:text-5xl text-white font-bold tracking-tight">
                Publish to your website{" "}
                <span className="relative whitespace-nowrap">
                  <span className="relative bg-gradient-to-r from-orange-400 to-rose-500 bg-clip-text text-transparent">
                    on autopilot
                  </span>
                </span>
              </h2>
              <p className="text-xl text-slate-300 leading-relaxed">
                Seamlessly integrate with WordPress, Ghost, or our API. Publish instantly or save as a draft, giving you
                full control over your blog automation.
              </p>
            </div>

            {/* Features list */}
            <motion.div
              variants={container}
              initial="hidden"
              whileInView="show"
              viewport={{ once: true }}
              className="space-y-6"
            >
              {[
                { text: "WordPress", icon: Blocks },
                { text: "Ghost", icon: Blocks },
                { text: "API Integration", icon: Code2 },
              ].map((feature, index) => (
                <motion.div key={feature.text} variants={item} className="flex items-center gap-4 group">
                  <span className="flex items-center justify-center w-12 h-12 rounded-full bg-orange-500/10 text-orange-400 group-hover:bg-orange-500/20 transition-colors duration-200">
                    <Check className="w-6 h-6" />
                  </span>
                  <span className="text-xl text-slate-200 group-hover:text-white transition-colors">
                    {feature.text}
                  </span>
                </motion.div>
              ))}
            </motion.div>
          </motion.div>
        </div>
      </div>
    </section>
  )
}

